import { useScheduleEvents } from "../../../../app/contexts/schedule.events.context";
import { useSnackbar } from "../../../../app/contexts/snackbar.context";

const STUDY_TAB_VALUE = 0;

/**
 * Custom hook to handle country tab changes with unsaved changes validation
 * @param {Object} params - Hook parameters
 * @param {Array} params.countryValues - Array of country values
 * @param {number} params.value - Current tab value
 * @param {string} params.studyId - Study ID
 * @param {string} params.scheduleId - Schedule of Events ID
 * @param {Function} params.updateUrlWithNewQueryParams - Function to update URL query params
 * @param {Function} params.setValue - Function to set the current tab value
 * @param {Function} params.setSiteId - Function to set the site ID
 * @param {Function} params.setModalOpen - Function to control modal visibility
 * @param {Function} params.setModalButtons - Function to set modal buttons
 * @param {Function} params.setModalConfig - Function to set modal config
 * @returns {Function} handleCountryTabChange - Function to handle tab changes
 */
export const useCountryTabChange = ({
  countryValues,
  value,
  studyId,
  scheduleId,
  updateUrlWithNewQueryParams,
  setValue,
  setSiteId,
  setModalOpen,
  setModalButtons,
  setModalConfig,
}) => {
  // Get required functions from contexts
  const {
    hasSOEToSave,
    handleSOESave,
    updateSiteContractDateForTab,
    clearAllChangeLogs,
  } = useScheduleEvents();
  const { showSnackbar } = useSnackbar();

  // Function to perform the tab change
  const performTabChange = (tab) => {
    updateUrlWithNewQueryParams({ sg: tab, siteId: 1 });
    setValue(tab);
    setSiteId(1);
    
    // Update siteContractEffectiveDate for the new tab
    const newCountry = countryValues[tab];
    if (newCountry) {
      updateSiteContractDateForTab(newCountry.siteGroupId, 1);
    }
  };

  const handleCountryTabChange = async (_, tab) => {
    // Validate that the tab is valid and avoid unnecessary updates
    if (tab < 0 || tab >= countryValues.length || tab === value) {
      return;
    }

    const studyLevelTransition = tab === STUDY_TAB_VALUE || value === STUDY_TAB_VALUE;

    // Check if there are unsaved changes
    if (hasSOEToSave && studyLevelTransition) {
      // Configure modal
      setModalConfig({
        title: "You Have Unsaved Changes",
        message: "You have unsaved changes. Do you want to save before switching tabs?",
      });
      
      setModalButtons([
        {
          label: "Continue Without Saving",
          variant: "outlined",
          onClick: () => {
            clearAllChangeLogs(); // Clear changelogs and delete edited records from memory
            performTabChange(tab);
            setModalOpen(false);
          },
        },
        {
          label: "Save & Continue",
          variant: "primary",
          onClick: async () => {
            try {
              await handleSOESave(studyId, scheduleId, null, null);
              performTabChange(tab);
              setModalOpen(false);
            } catch (error) {
              console.error("Error saving plan:", error);
              showSnackbar("Failed to save changes", "error");
            }
          },
        },
      ]);
      
      setModalOpen(true);
    } else {
      // No unsaved changes, proceed directly
      performTabChange(tab);
    }
  };

  return handleCountryTabChange;
};

/**
 * Custom hook to handle site changes with unsaved changes validation
 * @param {Object} params - Hook parameters
 * @param {number} params.siteId - Current site ID
 * @param {Array} params.sites - Array of available sites
 * @param {Function} params.setSiteId - Function to set the site ID
 * @param {Function} params.setSiteNumber - Function to set the site number
 * @param {URLSearchParams} params.queryParams - URL query parameters
 * @param {Function} params.navigate - React Router navigate function
 * @param {Array} params.countryValues - Array of country values
 * @param {number} params.value - Current tab value
 * @param {string} params.studyId - Study ID
 * @param {string} params.scheduleId - Schedule of Events ID
 * @param {Function} params.setModalOpen - Function to control modal visibility
 * @param {Function} params.setModalButtons - Function to set modal buttons
 * @param {Function} params.setModalConfig - Function to set modal config
 * @returns {Function} handleSiteChange - Function to handle site changes
 */
export const useSiteTabChange = ({
  siteId,
  sites,
  setSiteId,
  setSiteNumber,
  queryParams,
  navigate,
  countryValues,
  value,
}) => {
  // Get required functions from contexts
  const { updateSiteContractDateForTab } = useScheduleEvents();

  // Function to perform the site change
  const performSiteChange = (newSiteId) => {
    setSiteId(newSiteId);
    // Update siteNumber based on the selected site
    const selectedSite = sites.find((site) => site.siteId === newSiteId);
    const newSiteNumber = selectedSite?.siteNumber;
    setSiteNumber(newSiteNumber);
    queryParams.set("siteId", newSiteId.toString());
    navigate({
      search: queryParams.toString(),
    });
    
    // Update siteContractEffectiveDate for the new site
    if (countryValues[value]) {
      updateSiteContractDateForTab(countryValues[value].siteGroupId, newSiteId);
    }
  };

  const handleSiteChange = async (e) => {
    const newSiteId = parseInt(e.target.value);
    
    // Don't process if the value is NaN (user cleared or invalid input)
    if (isNaN(newSiteId)) {
      return;
    }
    
    // Don't process if the siteId hasn't changed
    if (newSiteId === siteId) {
      return;
    }

    performSiteChange(newSiteId);
  };

  return handleSiteChange;
};